import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useEffect } from 'react'
import { Navigate, Route, Routes } from 'react-router-dom'
import { AppShell } from './components/layout/app-shell'
import { Skeleton } from './components/ui/skeleton'
import { InboxPage } from './features/conversations/inbox-page'
import { LoginPage } from './features/auth/login-page'
import { SettingsPage } from './features/settings/settings-page'
import { SystemPage } from './features/system/system-page'
import { api, setCsrfToken } from './lib/api'
import type { AuthSession } from './types'

function AuthenticatedApplication({
  username,
  role,
}: {
  username: string
  role: NonNullable<AuthSession['role']>
}) {
  const queryClient = useQueryClient()

  useEffect(() => {
    const source = new EventSource('/api/events')
    source.addEventListener('update', (event) => {
      try {
        const update = JSON.parse((event as MessageEvent).data) as { type?: string }
        if (update.type?.startsWith('message.') || update.type?.startsWith('conversation.')) {
          void queryClient.invalidateQueries({ queryKey: ['conversations'] })
          void queryClient.invalidateQueries({ queryKey: ['conversation'] })
          void queryClient.invalidateQueries({ queryKey: ['messages'] })
        }
        if (update.type?.startsWith('settings.')) {
          void queryClient.invalidateQueries({ queryKey: ['dualhook-settings'] })
          void queryClient.invalidateQueries({ queryKey: ['delivery-settings'] })
          void queryClient.invalidateQueries({ queryKey: ['auto-reply-settings'] })
        }
        if (update.type?.startsWith('system.')) {
          void queryClient.invalidateQueries({ queryKey: ['system-status'] })
          void queryClient.invalidateQueries({ queryKey: ['system-errors'] })
        }
      } catch {
        // Gli heartbeat o eventi futuri non devono interrompere il canale realtime.
      }
    })
    return () => source.close()
  }, [queryClient])

  return (
    <AppShell username={username} role={role}>
      <Routes>
        <Route path="/messages" element={<InboxPage />} />
        <Route path="/messages/:conversationId" element={<InboxPage />} />
        {role === 'admin' && <Route path="/settings" element={<SettingsPage />} />}
        {role === 'admin' && <Route path="/system" element={<SystemPage />} />}
        <Route path="*" element={<Navigate to="/messages" replace />} />
      </Routes>
    </AppShell>
  )
}

export function App() {
  const session = useQuery({ queryKey: ['session'], queryFn: api.session, staleTime: 60_000 })

  useEffect(() => setCsrfToken(session.data?.csrfToken), [session.data?.csrfToken])

  if (session.isLoading) {
    return (
      <div className="grid min-h-svh place-items-center p-6">
        <div className="w-full max-w-sm rounded-3xl border border-[var(--border)] bg-[var(--surface)] p-8 shadow-[var(--shadow-card)]">
          <Skeleton className="mx-auto size-14 rounded-2xl" />
          <Skeleton className="mx-auto mt-6 h-6 w-32" />
          <Skeleton className="mx-auto mt-3 h-4 w-48" />
        </div>
      </div>
    )
  }

  if (!session.data?.authenticated) return <LoginPage />
  return (
    <AuthenticatedApplication
      username={session.data.username ?? 'admin'}
      role={session.data.role ?? 'admin'}
    />
  )
}
